import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NetworkData } from '@/interface/Graph.inderface';
import { Axios } from '@/components/base-axios';
import { ConsumerProfileData, TrudentyData } from '@/interface/ReasonCode.interface';

type GraphState = {
  data?: NetworkData;
  demoConsumersData?: ConsumerProfileData[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error?: string;
  fetchedAt?: number;
};

const initialState: GraphState = { status: 'idle' };

type ThunkCfg = { state: { graph: GraphState } };
/** Fetch both identity network data and demo consumers data */
export const loadConsumerGraphData = createAsyncThunk<
  { networkData: NetworkData; consumersData: TrudentyData[] },
  void,
  ThunkCfg
>(
  'graph/loadConsumerGraphData',
  async () => {
    const [networkResp, consumersResp] = await Promise.all([
      Axios.get(`/consumers/demo_identity_network_data`),
      Axios.get(`/consumers/demo_merchant_consumers`),
    ]);
    return {
      networkData: networkResp.data,
      consumersData: consumersResp.data,
    };
  },
  {
    condition: (_, { getState }) => {
      const { fetchedAt } = getState().graph;
      const TTL_MS = 1 * 120 * 1000; // 120 minutes - 2 hours
      return !fetchedAt || Date.now() - fetchedAt > TTL_MS;
      // return !data || !demoConsumersData; // cancel if both already loaded
    },
  }
);

// OPTIONAL: use TTL instead of single-shot
// const TTL_MS = 10 * 60 * 1000;
// condition: () => !fetchedAt || Date.now() - fetchedAt > TTL_MS

const graphSlice = createSlice({
  name: 'graph',
  initialState,
  reducers: {
    clearGraph(state) {
      state.data = undefined;
      state.demoConsumersData = undefined;
      state.status = 'idle';
      state.error = undefined;
      state.fetchedAt = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadConsumerGraphData.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(
        loadConsumerGraphData.fulfilled,
        (
          state,
          action: PayloadAction<{
            networkData: NetworkData;
            consumersData: TrudentyData[];
          }>
        ) => {
          state.status = 'succeeded';
          state.data = action.payload.networkData;
          state.demoConsumersData = action.payload.consumersData;
          state.fetchedAt = Date.now();
        }
      )
      .addCase(loadConsumerGraphData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export const { clearGraph } = graphSlice.actions;
export const { reducer } = graphSlice;
export default graphSlice;
